import { useMemo, useState } from "react";
import { contrastRatio, hexToRgb, wcag } from "@/lib/design/color";
import { m } from "@/paraglide/messages";

function Badge({ label, pass }: { label: string; pass: boolean }) {
  return (
    <div
      className={
        pass
          ? "flex items-center justify-between rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2"
          : "flex items-center justify-between rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2"
      }
    >
      <span className="text-sm font-medium">{label}</span>
      <span
        className={
          pass
            ? "text-sm font-semibold text-emerald-700 dark:text-emerald-400"
            : "text-sm font-semibold text-rose-700 dark:text-rose-400"
        }
      >
        {pass ? m.cc_pass() : m.cc_fail()}
      </span>
    </div>
  );
}

function ColorInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const valid = hexToRgb(value) != null;
  return (
    <div className="space-y-1.5">
      <span className="block text-sm text-muted-foreground">{label}</span>
      <div className="flex items-center gap-2">
        <input
          type="color"
          aria-label={label}
          value={valid ? value : "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-12 shrink-0 cursor-pointer rounded-md border border-black/15 bg-transparent"
        />
        <input
          type="text"
          aria-label={`${label} hex value`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          spellCheck={false}
          className="w-full rounded-md border border-black/15 bg-white px-3 py-2 font-mono text-sm text-neutral-900"
        />
      </div>
    </div>
  );
}

export function ContrastChecker() {
  const [fg, setFg] = useState("#1a1a1a");
  const [bg, setBg] = useState("#ffffff");

  const res = useMemo(() => {
    const a = hexToRgb(fg);
    const b = hexToRgb(bg);
    if (!a || !b) return null;
    return wcag(contrastRatio(a, b));
  }, [fg, bg]);

  return (
    <div className="sci-card rounded-2xl space-y-5 p-6 sm:p-8">
      <div className="grid gap-4 sm:grid-cols-2">
        <ColorInput label={m.cc_text()} value={fg} onChange={setFg} />
        <ColorInput label={m.cc_bg()} value={bg} onChange={setBg} />
      </div>

      <div
        className="flex items-center justify-center rounded-xl px-6 py-8"
        style={{ backgroundColor: hexToRgb(bg) ? bg : "#fff" }}
      >
        <span
          className="text-center text-2xl font-semibold"
          style={{ color: hexToRgb(fg) ? fg : "#000" }}
        >
          {m.cc_preview()}
        </span>
      </div>

      {res ? (
        <>
          <div className="sci-result-zone flex items-baseline justify-between gap-4 rounded-xl px-5 py-4">
            <span className="text-sm font-medium text-muted-foreground">
              {m.cc_ratio()}
            </span>
            <span
              data-testid="cc-ratio"
              className="sci-result font-display text-3xl font-bold sm:text-4xl"
            >
              {res.ratio.toFixed(2)}:1
            </span>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <Badge label={m.cc_aa_normal()} pass={res.aaNormal} />
            <Badge label={m.cc_aa_large()} pass={res.aaLarge} />
            <Badge label={m.cc_aaa_normal()} pass={res.aaaNormal} />
            <Badge label={m.cc_aaa_large()} pass={res.aaaLarge} />
          </div>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">{m.cc_hint()}</p>
      )}
    </div>
  );
}
